import clsx from 'clsx'
import { nothing, TemplateResult, unsafeCSS } from 'lit'
import { property, state } from 'lit/decorators.js'
import { ifDefined } from 'lit/directives/if-defined.js'
import { styleMap } from 'lit/directives/style-map.js'
import { html } from 'lit/static-html.js'
import { translate } from 'lit-i18n'

import { ActionType, AlertType, SnackbarPosition } from '@/common/types'

import { DdsElement } from '../../internal/dds-hu-element'
import DapDSButton from '../button/button.component'
import DapDSIcon from '../icon/icon.component'
import DapDSIconButton from '../icon-button/icon-button.component'
import DapDSLink from '../link/link.component'
import DapDSStack from '../stack/stack.component'
import styles from './snackbar-message.scss?inline'

/**
 * `dap-ds-snackbar-message`
 * @summary The Snackbar component shows a sliding message to the user.
 * @element dap-ds-snackbar-message
 * @title - Snackbar message
 * @group snackbar
 *
 * @event {{ id: string }} dds-close - Fires when the snackbar closes.
 *
 * @slot default - The content of the snackbar.
 *
 * @csspart base - Main snackbar container.
 * @csspart text - The text part of the snackbar.
 * @csspart closebutton - The close button part of the component.
 * @csspart icon - The icon part of the close button.
 *
 * @cssproperty --dds-snackbar-width - Width of the snackbar (default: clamp(var(--dds-spacing-6000), 20vw, var(--dds-containers-xsmall)))
 * @cssproperty --dds-snackbar-gap - Gap between snackbar elements (default: var(--dds-spacing-400))
 * @cssproperty --dds-snackbar-padding - Padding inside the snackbar (default: var(--dds-spacing-400))
 * @cssproperty --dds-snackbar-margin-bottom - Bottom margin of the snackbar (default: var(--dds-spacing-400))
 * @cssproperty --dds-snackbar-transition - Transition property for the snackbar (default: transform var(--dds-transition-slow) var(--dds-easing-ease-out)allow-discrete, opacity var(--dds-transition-slow) var(--dds-easing-ease-out) allow-discrete, margin var(--dds-transition-slow) var(--dds-easing-ease-out) allow-discrete, box-shadow var(--dds-transition-slow) var(--dds-easing-ease-out) allow-discrete)
 * @cssproperty --dds-snackbar-border-radius - Border radius of the snackbar (default: var(--dds-radius-base))
 * @cssproperty --dds-snackbar-font-size - Font size used in the snackbar (default: var(--dds-font-sm))
 * @cssproperty --dds-snackbar-background - Background color of the snackbar (default: var(--dds-snackbar-error-background))
 * @cssproperty --dds-snackbar-border - Border color of the snackbar (default: var(--dds-snackbar-error-border))
 * @cssproperty --dds-snackbar-text - Text color of the snackbar (default: var(--dds-snackbar-variant-text))
 * @cssproperty --dds-snackbar-icon - Icon color of the snackbar (default: var(--dds-snackbar-variant-icon))
 * @cssproperty --dds-snackbar-link - Link color of the snackbar (default: var(--dds-snackbar-variant-link))
 * @cssproperty --dds-snackbar-information-background - Background color for information variant (default: var(--dds-background-informative-medium))
 * @cssproperty --dds-snackbar-information-border - Border color for information variant (default: var(--dds-border-width-base) solidvar(--dds-border-informative-subtle))
 * @cssproperty --dds-snackbar-information-icon - Icon color for information variant (default: var(--dds-icon-informative-subtle))
 * @cssproperty --dds-snackbar-success-background - Background color for success variant (default: var(--dds-background-positive-medium))
 * @cssproperty --dds-snackbar-success-border - Border color for success variant (default: var(--dds-border-width-base) solidvar(--dds-border-positive-subtle))
 * @cssproperty --dds-snackbar-success-icon - Icon color for success variant (default: var(--dds-icon-positive-subtle))
 * @cssproperty --dds-snackbar-error-background - Background color for error variant (default: var(--dds-background-negative-medium))
 * @cssproperty --dds-snackbar-error-border - Border color for error variant (default: var(--dds-border-width-base) solidvar(--dds-border-negative-subtle))
 * @cssproperty --dds-snackbar-error-icon - Icon color for error variant (default: var(--dds-icon-negative-subtle))
 * @cssproperty --dds-snackbar-variant-text - Text color for information, success, and error variants (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-snackbar-variant-icon - Icon color for information, success, and error variants (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-snackbar-variant-link - Link color for information, success, and error variants (default: var(--dds-text-neutral-base))
 * @cssproperty --dds-snackbar-animation-duration - Duration of entrance animations (default: 0.3s)
 * @cssproperty --dds-snackbar-animation-timing - Timing function for animations (default: ease-out)
 */
export default class DapDSSnackbarMessage extends DdsElement {
  static tagName = 'dap-ds-snackbar-message'

  static dependencies = {
    'dap-ds-stack': DapDSStack,
    'dap-ds-button': DapDSButton,
    'dap-ds-link': DapDSLink,
    'dap-ds-icon': DapDSIcon,
    'dap-ds-icon-button': DapDSIconButton,
  }

  /** The actions of the snackbar item. */
  @property({ type: Array }) actions?: ActionType[] = []
  /** The message of the snackbar item. */
  @property({ type: String }) message = ''
  /** Whether the snackbar has a close button. */
  @property({ type: String, reflect: true }) closeButton = 'true'
  /** The message type of the snackbar item. */
  @property({ type: String }) alertType: AlertType = 'default'
  /** The custom style of the snackbar item. */
  @property({ type: Object }) customStyle?: Record<string, string>
  /** The position of the snackbar container for directional animations. */
  @property({ type: String }) position: SnackbarPosition = 'bottom-right'
  /** `data-testid` for the snackbar close button. */
  @property() closeButtonTestId = 'snackbar-close-button'

  @state() private isClosing = false

  static readonly styles = unsafeCSS(styles)

  private getActions(): TemplateResult | typeof nothing {
    if (this.actions && this.actions.length > 0) {
      return html`<dap-ds-stack
        part="actions"
        exportparts="base:actions-base"
        class="snackbar__actions"
        direction="row">
        ${this.actions.map((actionItem: ActionType, index: number) => {
          if (actionItem && typeof actionItem.func === 'function') {
            return html`<dap-ds-button
              variant=${actionItem.variant ?? 'primary'}
              data-testid=${`snackbar-action-${index}`}
              @click=${() => {
                if (actionItem.func) {
                  actionItem.func()
                }
              }}
              >${ifDefined(actionItem.text)}</dap-ds-button
            >`
          } else {
            return html`<dap-ds-link
              class="snackbar__link"
              href=${ifDefined(actionItem.href)}
              target="${ifDefined(actionItem.target)}"
              rel=${ifDefined(actionItem.target ? actionItem.rel : undefined)}
              >${ifDefined(actionItem.text)}</dap-ds-link
            >`
          }
        })}
      </dap-ds-stack>`
    }
    return nothing
  }

  private getAlertTypeIcon(): TemplateResult | typeof nothing {
    let iconName = ''
    switch (this.alertType) {
      case 'information':
        iconName = 'information-2-fill'
        break
      case 'successful':
        iconName = 'checkbox-circle-fill'
        break
      case 'error':
        iconName = 'error-warning-fill'
        break
      default:
    }
    return iconName.length > 0
      ? html`<dap-ds-icon
          name=${iconName}
          size="lg"
          class="snackbar__alert-icon"></dap-ds-icon>`
      : nothing
  }

  private _close(id: string) {
    if (id) {
      this.isClosing = true
      // Wait for the closing animation to complete before emitting the close event
      setTimeout(() => {
        this.emit('dds-close', { id })
      }, 300) // Match the animation duration
    }
  }

  render() {
    return html`<div
      role="alert"
      aria-label=${this.message}
      part="base"
      class=${clsx(
        'snackbar',
        `snackbar--${this.alertType}`,
        `snackbar--position-${this.position}`,
        { 'snackbar--closing': this.isClosing },
      )}
      style=${styleMap(this.customStyle ?? {})}>
      <div class="snackbar__main">
        <div class="snackbar__left-group">
          ${this.getAlertTypeIcon()}
          <div class="snackbar__content">
            ${this.message
              ? html`<span part="text" class="snackbar__text"
                  >${this.message}</span
                >`
              : html`<slot></slot>`}
            ${this.actions ? html` ${this.getActions()}` : nothing}
          </div>
        </div>
        ${this.closeButton === 'true'
          ? html`<dap-ds-icon-button
              @click=${() => this._close(this.id)}
              part="closebutton"
              data-testid=${this.closeButtonTestId}
              class="snackbar__close"
              aria-label=${translate('close')}>
              <dap-ds-icon
                part="close-icon"
                class="snackbar__close-icon"
                name="close-line"></dap-ds-icon>
            </dap-ds-icon-button>`
          : nothing}
      </div>
    </div>`
  }
}
